import React, { useEffect, useState } from 'react';
import { Spin, Select, Modal } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import { XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Area, AreaChart, Brush } from 'recharts';
import dayjs from 'dayjs';
import '../styles/Ambientali.css';
import { IoEarth } from "react-icons/io5";
import { fetchEnvironmentData, fetchEnvironmentDailyData, fetchEnvironmentIndex } from '../APIs/apiService';
function Ambientali({ arco, selectedDate: parentSelectedDate, radarSelected }) {
  const [mediaList, setMediaList] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(parentSelectedDate || null);
  const [selectedMetric, setSelectedMetric] = useState('temperature');
  const [isExpanded, setIsExpanded] = useState(false);
  const [useNewFormat, setUseNewFormat] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Metric options for the dropdown - updated for new format
  const metricOptions = [
    { value: 'temperature', label: 'Temperatura (°C)', newField: 'Temperatura [°C]', oldPath: ['SCD30', 'temperature'], color: '#FF8C42' },
    { value: 'humidity', label: 'Umidità (%)', newField: 'Umidità [%]', oldPath: ['SCD30', 'humidity'], color: '#38A3A5' },
    { value: 'pressure', label: 'Pressione (hPa)', newField: 'Pressione [hPa]', oldPath: ['BME688', 'atmospheric_pressure'], color: '#95E1D3' },
    { value: 'formaldehyde', label: 'Formaldeide (ppb)', newField: 'Formaldeide [ppb]', oldPath: ['SFA30', 'formaldehyde_ug_m3'], color: '#FFABAB' },
    { value: 'pm25', label: 'PM₂.₅ (µg/m³)', newField: 'PM₂.₅ [µg/m³]', oldPath: ['SN5', 'pm25_ug_m3'], color: '#C7CEEA' },
    { value: 'no2', label: 'NO₂ (ppm)', newField: 'NO₂ [ppm]', oldPath: ['MultiChannelGas', 'NO2_PPM'], color: '#D5AAFF' },
    { value: 'c2h5ch', label: 'C₂H₅CH (ppm)', newField: 'C₂H₅CH [ppm]', oldPath: ['MultiChannelGas', 'C2H5OH_PPM'], color: '#FF6B6B' },
    { value: 'voc', label: 'VOC (ppm)', newField: 'VOC [ppm]', oldPath: ['SN5', 'voc_index'], color: '#4ECDC4' }
  ];

  useEffect(() => {
    setIsLoading(true);
    // Try new format first using centralized API service
    fetchEnvironmentData()
      .then(data => {
        setUseNewFormat(true);
        // Extract unique dates from the new format
        const allDates = [...new Set(data.map(item => {
          const timestamp = new Date(parseInt(item.time) * 1000);
          return timestamp.toISOString().split('T')[0];
        }))].sort();

        // Filter dates to show only 10/05/2025 - 18/05/2025
        const startDate = '2025-05-10';
        const endDate = '2025-05-18';
        const dates = allDates.filter(date => date >= startDate && date <= endDate);

        const formattedDates = dates.map(date => ({ data: date }));
        setMediaList(formattedDates);

        if (dates.length > 0) {
          const firstDate = dates[0]; // Set first date as default
          setSelectedDate(firstDate);
          fetchDayDataNew(firstDate, data);
        }
        setIsLoading(false);
      })
      .catch(() => {
        // Fallback to old format
        console.warn("Failed to load Environment_1h.json, falling back to ambientali_finale_tutto");
        setUseNewFormat(false);
        fetchEnvironmentIndex()
          .then(data => {
            setMediaList(data || []);
            if (data && data.length > 0) {
              const firstDate = data[0].data;
              setSelectedDate(firstDate);
              fetchDayDataOld(firstDate);
            }
            setIsLoading(false);
          })
          .catch(err => {
            console.error("Errore nel caricamento dei dati ambientali:", err);
            setIsLoading(false);
          });
      });
  }, []);

  const fetchDayDataNew = (dateStr, allData) => {
    // Filter data for the selected date
    const filteredData = allData.filter(item => {
      const timestamp = new Date(parseInt(item.time) * 1000);
      const itemDate = timestamp.toISOString().split('T')[0];
      return itemDate === dateStr;
    });
    setSelectedData(filteredData);
  };

  const fetchDayDataOld = (dateStr) => {
    const formattedDate = dateStr.replace(/-/g, ''); // Convert YYYY-MM-DD to YYYYMMDD
    fetchEnvironmentDailyData(formattedDate)
      .then(data => {
        setSelectedData(data || null);
      })
      .catch(err => {
        console.error('Error fetching day data:', err);
        setSelectedData(null);
      });
  };

  const formatValue = (obj) => {
    if (!obj || obj.v === undefined || obj.v === null) return "N/D";
    let unit = obj.u;
    if (unit === "DEGREE_CELSIUS" || unit === "DEGREE_CELCIUS") unit = "°C";
    else if (unit === "PERCENTAGE") unit = "%";
    return `${obj.v} ${unit}`;
  };

  // Fetch day data when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      if (useNewFormat) {
        // Re-fetch all data and filter by date using centralized API service
        fetchEnvironmentData()
          .then(data => fetchDayDataNew(selectedDate, data))
          .catch(err => console.error('Error fetching data:', err));
      } else {
        fetchDayDataOld(selectedDate);
      }
    }
  }, [selectedDate, useNewFormat]);

  // Get the current selected metric configuration
  const getCurrentMetric = () => {
    return metricOptions.find(m => m.value === selectedMetric) || metricOptions[0];
  };

  // Extract real chart data from selected day's data
  const generateChartData = () => {
    if (!selectedData || selectedData.length === 0) {
      return [];
    }

    // Create full time range from 00:00 to 23:00
    const fullTimeRange = [];
    for (let hour = 0; hour < 24; hour++) {
      fullTimeRange.push(`${hour.toString().padStart(2, '0')}:00`);
    }

    // Initialize data map with all time slots
    const dataMap = new Map();
    fullTimeRange.forEach(time => {
      dataMap.set(time, {
        time,
        value: null
      });
    });

    const metric = getCurrentMetric();

    if (useNewFormat) {
      // Process new format data
      selectedData.forEach((item) => {
        const timestamp = new Date(parseInt(item.time) * 1000);
        const hourKey = `${timestamp.getHours().toString().padStart(2, '0')}:00`;

        // Get value from new format field
        const fieldValue = item[metric.newField];
        const numericValue = fieldValue ? parseFloat(fieldValue) : null;

        if (numericValue !== null && !isNaN(numericValue) && dataMap.has(hourKey)) {
          // If there's already a value for this hour, average them
          const existing = dataMap.get(hourKey);
          if (existing.value !== null) {
            dataMap.set(hourKey, {
              time: hourKey,
              value: (existing.value + numericValue) / 2
            });
          } else {
            dataMap.set(hourKey, {
              time: hourKey,
              value: numericValue
            });
          }
        }
      });
    } else {
      // Process old format data
      if (selectedData.acquisizioni) {
        selectedData.acquisizioni.forEach((acquisition) => {
          const time = acquisition.ora; // Format: "HH:MM"
          const hourKey = time.substring(0, 2) + ':00'; // Convert HH:MM to HH:00

          // Navigate through the nested structure using dataPath
          let value = acquisition.valori;
          for (const key of metric.oldPath) {
            if (value && value[key]) {
              value = value[key];
            } else {
              value = null;
              break;
            }
          }

          // Extract the numeric value
          let numericValue = null;
          if (value && typeof value === 'object' && value.v !== undefined) {
            numericValue = value.v;
          }

          if (numericValue !== null && dataMap.has(hourKey)) {
            dataMap.set(hourKey, {
              time: hourKey,
              value: parseFloat(numericValue)
            });
          }
        });
      }
    }

    return Array.from(dataMap.values());
  };

  const chartData = generateChartData();

  // Render chart component
  const renderChart = (height = 200) => (
    <>
      {selectedData && chartData.length > 0 && (
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id={`color${selectedMetric}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={getCurrentMetric().color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={getCurrentMetric().color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              interval="preserveStartEnd"
              ticks={['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '23:00']}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#666' }}
              axisLine={{ stroke: '#e0e0e0' }}
              domain={['auto', 'auto']}
            />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="value"
              stroke={getCurrentMetric().color}
              strokeWidth={2}
              fill={`url(#color${selectedMetric})`}
              connectNulls={true}
            />
            <Brush
              dataKey="time"
              height={30}
              stroke="#8884d8"
              fill="#f5f5f5"
              travellerWidth={10}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </>
  );

  // Only show this component if radar is selected
  if (!radarSelected) {
    return (
      <div className="ambientali-wrapper">
        <div className="ambientali-content" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#999', fontSize: '14px' }}>
            Seleziona il radar sulla mappa per visualizzare i dati ambientali.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="proprieta-wrapper">
        <div className="proprieta-title">
          <IoEarth />
          <h3>Media giornaliera ambientale</h3>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <Spin size="large" tip="Caricamento dati ambientali..." />
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="proprieta-wrapper">
      <div className="proprieta-title">
        <IoEarth />
        <h3>Media giornaliera ambientale</h3>
      </div>
      <div className="proprieta-content anomaly-content">
        <p className="proprieta-description anomaly-description">
          Seleziona la data per la quale vuoi visualizzare i valori.
        </p>

        <Select
          placeholder="Seleziona data"
          className="anomaly-date-picker"
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          getPopupContainer={(trigger) => trigger.parentElement}
          popupStyle={{ zIndex: 9999 }}
          style={{ borderRadius: "20px" }}
        >
          {mediaList.map(item => (
            <Select.Option key={item.data} value={item.data}>
              {dayjs(item.data).format('DD/MM/YYYY')}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className="proprieta-content anomaly-content">
        <Select
          value={selectedMetric}
          onChange={setSelectedMetric}
          style={{ width: '100%', marginBottom: '16px' }}
          options={metricOptions}
          placeholder="Seleziona metrica"
          getPopupContainer={(trigger) => trigger.parentElement}
          popupStyle={{ zIndex: 9999 }}
        />
      </div>
      <div style={{ position: 'relative' }}>
        {selectedData && chartData.length > 0 && (
          <button
            onClick={() => setIsExpanded(true)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '36px',
              height: '36px',
              backgroundColor: 'rgba(24, 144, 255, 0.9)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              zIndex: 100,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(64, 169, 255, 1)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(24, 144, 255, 0.9)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title="Espandi grafico"
          >
            <ExpandOutlined />
          </button>
        )}
        {renderChart()}
      </div>
      {selectedData && chartData.length === 0 && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
          Nessun dato disponibile per la metrica selezionata
        </div>
      )}
    </div>

    {/* Expanded Chart Modal */}
    <Modal
      open={isExpanded}
      onCancel={() => setIsExpanded(false)}
      footer={null}
      width="95%"
      centered
      title="Dati Ambientali - Vista Espansa"
      destroyOnClose
      zIndex={9999}
      style={{ top: 20 }}
    >
      <div style={{ padding: '20px 0' }}>
        {renderChart(500)}
      </div>
    </Modal>
    </>
  );
}

export default Ambientali;
